import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Send, EyeOff, MessageSquareWarning, Search, Share2 } from "lucide-react";
import { api, formatError } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";
import { toast } from "sonner";

export const STATUS_STYLE = {
  PUBLISHED: "bg-leaf/10 text-leaf", PENDING: "bg-gold/15 text-amber-700", REJECTED: "bg-coral/15 text-coral-hover",
  DRAFT: "bg-secondary text-ink-soft", ARCHIVED: "bg-secondary text-ink-soft",
};
const LABEL = { PUBLISHED: "Live", PENDING: "In review", REJECTED: "Needs changes", DRAFT: "Draft", ARCHIVED: "Archived" };

export function KitchenRecipes({ recipes, chefSlug, onlyDrafts = false, reload }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [order, setOrder] = useState(null);
  const list = (order || recipes).filter((r) => (!onlyDrafts || r.status === "DRAFT") && r.title.toLowerCase().includes(q.toLowerCase()));

  const iconBtn = "grid place-items-center w-11 h-11 rounded-full border border-border hover:border-coral hover:text-coral transition-colors disabled:opacity-30";
  const call = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };
  const move = async (idx, dir) => {
    const arr = [...(order || recipes)];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setOrder(arr);
    try { await api.put("/kitchen/recipes/reorder", { ids: arr.map((r) => r.id) }); } catch { toast.error("Could not save order"); }
  };
  const share = (r) => window.open(`https://wa.me/?text=${encodeURIComponent(`${r.title} · ${window.location.origin}/chefs/${chefSlug}/${r.slug}`)}`, "_blank");

  return (
    <div data-testid={onlyDrafts ? "kitchen-drafts" : "kitchen-recipes"}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="kitchen-recipe-search" placeholder="Search your recipes…"
            className="h-11 w-64 max-w-full rounded-full border border-border bg-white pl-9 pr-4 outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium text-base" />
        </div>
        <button onClick={() => navigate("/kitchen/recipes/new")} data-testid="new-recipe-button" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-coral hover:bg-coral-hover text-white font-bold transition-colors"><Plus size={18} /> New recipe</button>
      </div>
      <div className="rounded-3xl bg-white border border-border shadow-soft overflow-hidden">
        {list.length === 0 ? (
          <div className="p-10 text-center text-ink-soft">{onlyDrafts ? "No drafts. Unfinished recipes you save will appear here." : q ? `No recipes match "${q}".` : "No recipes yet — create your first one!"}</div>
        ) : (
          <div className="divide-y divide-border">
            {list.map((r, idx) => (
              <div key={r.id} data-testid={`kitchen-recipe-${r.slug}`} className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-4">
                {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0 border border-border" /> : <div className="w-16 h-16 rounded-xl bg-secondary grid place-items-center text-xs font-bold text-ink-soft shrink-0">No photo</div>}
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-black text-ink truncate">{r.status === "PUBLISHED" ? <Link to={`/chefs/${chefSlug}/${r.slug}`} className="hover:text-coral">{r.title}</Link> : r.title}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_STYLE[r.status]}`} data-testid={`status-${r.slug}`}>{LABEL[r.status] || r.status}</span>
                    <span className="text-xs text-ink-soft">★ {r.rating_avg} · {Number(r.rating_count || 0).toLocaleString()} · {Number(r.views || 0).toLocaleString()} views</span>
                  </div>
                  {r.status === "REJECTED" && r.moderation_note && <p className="text-xs text-coral-hover mt-1 flex items-center gap-1"><MessageSquareWarning size={13} /> {r.moderation_note}</p>}
                </div>
                <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end shrink-0 flex-wrap">
                  {!onlyDrafts && !q && (
                    <>
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className={iconBtn} title="Move up" aria-label="Move up" data-testid={`move-up-${r.slug}`}><ArrowUp size={16} /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className={iconBtn} title="Move down" aria-label="Move down" data-testid={`move-down-${r.slug}`}><ArrowDown size={16} /></button>
                    </>
                  )}
                  {(r.status === "DRAFT" || r.status === "REJECTED") && (
                    <button onClick={() => call(() => api.post(`/kitchen/recipes/${r.id}/submit`), "Sent for review — we'll notify you when it's live")} data-testid={`submit-${r.slug}`}
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-ink text-white text-sm font-bold hover:bg-ink-soft"><Send size={14} /> Go live</button>
                  )}
                  {r.status === "PUBLISHED" && (
                    <button onClick={() => call(() => api.post(`/kitchen/recipes/${r.id}/unpublish`), "Moved to drafts")} className={iconBtn} title="Unpublish" aria-label="Unpublish" data-testid={`unpublish-${r.slug}`}><EyeOff size={16} /></button>
                  )}
                  {r.status === "PUBLISHED" && <button onClick={() => share(r)} className={iconBtn} title="Share on WhatsApp" aria-label="Share" data-testid={`share-${r.slug}`}><Share2 size={16} /></button>}
                  <button onClick={() => navigate(`/kitchen/recipes/${r.id}/edit`)} className={iconBtn} title="Edit" aria-label="Edit" data-testid={`edit-recipe-${r.slug}`}><Pencil size={16} /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-recipe-${r.slug}`} className={iconBtn} title="Delete" aria-label="Delete"><Trash2 size={16} /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this recipe?</AlertDialogTitle>
                        <AlertDialogDescription>This can't be undone. "{r.title}" will be removed.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => call(() => api.delete(`/recipes/${r.id}`), "Recipe deleted")} className="bg-coral hover:bg-coral-hover" data-testid="confirm-delete">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
